import React, { useEffect, useMemo, useState } from 'react';

import {
    Alert,
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    Grid,
    IconButton,
    InputAdornment,
    MenuItem,
    OutlinedInput,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    useTheme
} from '@mui/material';

import {
    IconBell,
    IconBriefcase,
    IconCalendar,
    IconCalendarEvent,
    IconClock,
    IconEdit,
    IconHistory,
    IconPlayerPlay,
    IconPlus,
    IconSearch,
    IconStar,
    IconTrash,
    IconX,
    IconArrowLeft
} from '@tabler/icons';
import { IconArrowAutofitRight, IconRotateClockwise } from '@tabler/icons-react';

import { motion } from 'framer-motion';
import api from '../../../../services/api';
import { useHistory, useParams } from 'react-router-dom';

const MotionCard = motion(Card);

// ──────────────────────────────────────────────────────────────
// Constants
// ──────────────────────────────────────────────────────────────

const TASK_ICONS = {
    'automations.tasks.calendar_reminder': IconCalendarEvent,
    'automations.tasks.deals_followup': IconBriefcase,
    'automations.tasks.notification_cleanup': IconBell,
    'automations.tasks.send_campaign': IconArrowAutofitRight,
    'automations.tasks.trigger_campaig': IconArrowAutofitRight,
    'automations.tasks.sync_products': IconRotateClockwise,
    'automations.tasks.extract_reports': IconRotateClockwise,
    'automations.tasks.human_timeout_passback': IconClock
};

const TASK_COLORS = {
    'automations.tasks.calendar_reminder': '#ea580c',
    'automations.tasks.deals_followup': '#2563eb',
    'automations.tasks.notification_cleanup': '#7c3aed',
    'automations.tasks.send_campaign': '#16a34a',
    'automations.tasks.trigger_campaign': '#16a34a',
    'automations.tasks.sync_products': '#d97706',
    'automations.tasks.extract_reports': '#059669',
    'automations.tasks.human_timeout_passback': '#0284c7'
};

const TASK_EXTRA_FIELDS = {
    'automations.tasks.deals_followup': [{ key: 'days_stale', label: 'Dias sem atualização para alertar', type: 'number', default: 7 }],
    'automations.tasks.notification_cleanup': [
        { key: 'days_old', label: 'Notificações mais antigas que (dias)', type: 'number', default: 30 }
    ],
    'automations.tasks.send_campaign': [{ key: 'campaign_id', label: 'ID da campanha a enviar', type: 'text', default: '' }],
    'automations.tasks.sync_products': [{ key: 'external_system', label: 'Sistema externo', type: 'text', default: '' }],
    'automations.tasks.extract_reports': [{ key: 'report_type', label: 'Tipo de relatório', type: 'text', default: '' }],
    'automations.tasks.human_timeout_passback': [
        { key: 'hours_idle', label: 'Horas parado até voltar pro bot', type: 'number', default: 4 }
    ]
};

const STATUS_CONFIG = {
    running: { label: 'Rodando', color: '#f59e0b' },
    success: { label: 'Sucesso', color: '#16a34a' },
    failure: { label: 'Falha', color: '#ef4444' }
};

const EMPTY_FORM = {
    name: '',
    description: '',
    task_name: '',
    schedule_type: 'interval',
    interval_every: 1,
    interval_period: 'hours',
    crontab_minute: '0',
    crontab_hour: '8',
    crontab_day_of_week: '*',
    crontab_day_of_month: '*',
    crontab_month_of_year: '*',
    kwargs: {},
    active: true
};

// ──────────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────────

const describeSchedule = (a) => {
    if (a.schedule_type === 'interval') {
        const labels = { seconds: 'segundo(s)', minutes: 'minuto(s)', hours: 'hora(s)', days: 'dia(s)' };
        return `A cada ${a.interval_every} ${labels[a.interval_period] || a.interval_period}`;
    }
    return `Cron: ${a.crontab_minute} ${a.crontab_hour} ${a.crontab_day_of_week} ${a.crontab_day_of_month} ${a.crontab_month_of_year}`;
};

const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

// ──────────────────────────────────────────────────────────────
// Component
// ──────────────────────────────────────────────────────────────

const Automations = () => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    // state
    const [appId, setAppId] = useState(null);
    const [choices, setChoices] = useState({ tasks: [], period_choices: [] });
    const [automations, setAutomations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [dateFilter, setDateFilter] = useState('');
    const [status, setStatus] = useState({ type: '', message: '' });

    // create/edit
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);

    // run now
    const [runningId, setRunningId] = useState(null);

    // delete
    const [deleteId, setDeleteId] = useState(null);
    const [deleting, setDeleting] = useState(false);

    // history
    const [historyOpen, setHistoryOpen] = useState(false);
    const [historyAutomation, setHistoryAutomation] = useState(null);
    const [executions, setExecutions] = useState([]);
    const [executionsLoading, setExecutionsLoading] = useState(false);

    const history = useHistory();

    // ── load app ─────────────────────────────────────────────
    useEffect(() => {
        api.get('v1/apps/default/')
            .then((r) => setAppId(r.data?.app?.id || null))
            .catch(console.error);
    }, []);

    // ── load choices ─────────────────────────────────────────
    useEffect(() => {
        if (!appId) return;
        api.get(`v1/apps/${appId}/automations/choices/`)
            .then((r) => setChoices(r.data))
            .catch(console.error);
    }, [appId]);

    // ── load automations ─────────────────────────────────────
    useEffect(() => {
        if (!appId) return;
        async function load() {
            setLoading(true);
            try {
                const r = await api.get(`v1/apps/${appId}/automations/`);
                setAutomations(r.data || []);
            } catch {
                setStatus({ type: 'error', message: 'Erro ao carregar automações.' });
            } finally {
                setLoading(false);
            }
        }
        load();
    }, [appId]);

    // ── filtered list ─────────────────────────────────────────
    const filtered = useMemo(
        () =>
            automations.filter((a) => {
                const matchSearch =
                    !search ||
                    a.name.toLowerCase().includes(search.toLowerCase()) ||
                    a.task_label?.toLowerCase().includes(search.toLowerCase());
                const matchDate = !dateFilter || a.created_at?.startsWith(dateFilter);
                return matchSearch && matchDate;
            }),
        [automations, search, dateFilter]
    );

    // ── form helpers ─────────────────────────────────────────
    const setField = (key, value) => setFormData((prev) => ({ ...prev, [key]: value }));
    const setKwarg = (key, value) => setFormData((prev) => ({ ...prev, kwargs: { ...prev.kwargs, [key]: value } }));

    const openCreate = () => {
        setEditingId(null);
        setFormData(EMPTY_FORM);
        setDialogOpen(true);
    };

    const openEdit = (a) => {
        setEditingId(a.id);
        setFormData({
            name: a.name,
            description: a.description || '',
            task_name: a.task_name,
            schedule_type: a.schedule_type,
            interval_every: a.interval_every ?? 1,
            interval_period: a.interval_period ?? 'hours',
            crontab_minute: a.crontab_minute,
            crontab_hour: a.crontab_hour,
            crontab_day_of_week: a.crontab_day_of_week,
            crontab_day_of_month: a.crontab_day_of_month,
            crontab_month_of_year: a.crontab_month_of_year,
            kwargs: a.kwargs || {},
            active: a.active
        });
        setDialogOpen(true);
    };

    // ── save ─────────────────────────────────────────────────
    const handleSave = async () => {
        if (!formData.name || !formData.task_name) {
            setStatus({ type: 'error', message: 'Nome e tipo de tarefa são obrigatórios.' });
            return;
        }
        setSaving(true);
        try {
            if (editingId) {
                const r = await api.put(`v1/apps/${appId}/automations/${editingId}/`, formData);
                setAutomations((prev) => prev.map((a) => (a.id === editingId ? r.data : a)));
                setStatus({ type: 'success', message: 'Automação atualizada!' });
            } else {
                const r = await api.post(`v1/apps/${appId}/automations/`, formData);
                setAutomations((prev) => [r.data, ...prev]);
                setStatus({ type: 'success', message: 'Automação criada!' });
            }
            setDialogOpen(false);
        } catch (e) {
            setStatus({ type: 'error', message: e.response?.data?.error || 'Erro ao salvar automação.' });
        } finally {
            setSaving(false);
        }
    };

    // ── toggle active ─────────────────────────────────────────
    const handleToggleActive = async (a) => {
        try {
            const r = await api.patch(`v1/apps/${appId}/automations/${a.id}/`, { active: !a.active });
            setAutomations((prev) => prev.map((x) => (x.id === a.id ? r.data : x)));
        } catch {
            setStatus({ type: 'error', message: 'Erro ao atualizar status.' });
        }
    };

    // ── run now ──────────────────────────────────────────────
    const handleRunNow = async (a) => {
        setRunningId(a.id);
        try {
            await api.post(`v1/apps/${appId}/automations/${a.id}/run/`);
            setStatus({ type: 'success', message: `"${a.name}" enviada para execução!` });
        } catch {
            setStatus({ type: 'error', message: 'Erro ao executar automação.' });
        } finally {
            setRunningId(null);
        }
    };

    // ── delete ────────────────────────────────────────────────
    const handleDelete = async () => {
        if (!deleteId) return;
        setDeleting(true);
        try {
            await api.delete(`v1/apps/${appId}/automations/${deleteId}/`);
            setAutomations((prev) => prev.filter((a) => a.id !== deleteId));
            setDeleteId(null);
            setStatus({ type: 'success', message: 'Automação removida.' });
        } catch {
            setStatus({ type: 'error', message: 'Erro ao excluir automação.' });
        } finally {
            setDeleting(false);
        }
    };

    // ── history ──────────────────────────────────────────────
    const openHistory = async (a) => {
        setHistoryAutomation(a);
        setHistoryOpen(true);
        setExecutionsLoading(true);
        setExecutions([]);
        try {
            const r = await api.get(`v1/apps/${appId}/automations/${a.id}/executions/`);
            setExecutions(r.data || []);
        } catch {
            setExecutions([]);
        } finally {
            setExecutionsLoading(false);
        }
    };

    const extraFields = TASK_EXTRA_FIELDS[formData.task_name] || [];

    // ── render ────────────────────────────────────────────────
    return (
        <Box>
            {/* HERO */}
            <Button
                variant="outlined"
                startIcon={<IconArrowLeft size={18} />}
                onClick={() => history.goBack()}
                sx={{
                    borderRadius: '12px',
                    textTransform: 'none'
                }}
            >
                Voltar
            </Button>

            <Box
                sx={{
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: '30px',
                    marginTop: 2,
                    p: { xs: 3, md: 5 },
                    mb: 4,
                    background: isDark
                        ? 'linear-gradient(135deg,#111827 0%,#0f172a 100%)'
                        : 'linear-gradient(135deg,#f8fafc 0%,#ffffff 100%)',
                    border: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(15,23,42,0.06)',
                    boxShadow: isDark ? '0 10px 40px rgba(0,0,0,0.35)' : '0 10px 40px rgba(15,23,42,0.06)'
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        width: 320,
                        height: 320,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, rgba(59,130,246,0.18), rgba(168,85,247,0.15))',
                        top: -120,
                        right: -80,
                        filter: 'blur(50px)'
                    }}
                />

                <Box sx={{ position: 'relative', zIndex: 2 }}>
                    <Chip icon={<IconStar size={14} />} label="CRM Inteligente" sx={{ mb: 2, fontWeight: 700 }} />

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            mb: 1,
                            flexWrap: 'wrap',
                            gap: 2
                        }}
                    >
                        <Typography
                            variant="h1"
                            sx={{
                                fontWeight: 900,
                                fontSize: { xs: '2rem', md: '3rem' },
                                letterSpacing: '-2px',
                                background: 'linear-gradient(90deg,#2563eb,#7c3aed)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent'
                            }}
                        >
                            Automações
                        </Typography>

                        <Button
                            variant="contained"
                            startIcon={<IconPlus size={18} />}
                            onClick={openCreate}
                            sx={{
                                borderRadius: '16px',
                                px: 3,
                                height: 48,
                                textTransform: 'none',
                                fontWeight: 700,
                                background: 'linear-gradient(135deg,#2563eb,#7c3aed)'
                            }}
                        >
                            Nova automação
                        </Button>
                    </Box>

                    <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 700, lineHeight: 1.8, mb: 4 }}>
                        Crie tarefas automáticas agendadas: lembretes de calendário, follow-up de negócios, limpeza de notificações e muito
                        mais.
                    </Typography>

                    {/* FILTERS */}
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <OutlinedInput
                                fullWidth
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Buscar automações..."
                                startAdornment={
                                    <InputAdornment position="start">
                                        <IconSearch size={18} />
                                    </InputAdornment>
                                }
                                sx={{
                                    height: 56,
                                    borderRadius: '18px',
                                    background: isDark ? 'rgba(255,255,255,0.04)' : '#fff'
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                type="date"
                                value={dateFilter}
                                onChange={(e) => setDateFilter(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <IconCalendar size={18} />
                                        </InputAdornment>
                                    )
                                }}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '18px', height: 56 } }}
                            />
                        </Grid>
                    </Grid>
                </Box>
            </Box>

            {status.message && (
                <Alert severity={status.type} sx={{ mb: 3, borderRadius: '16px' }} onClose={() => setStatus({ type: '', message: '' })}>
                    {status.message}
                </Alert>
            )}

            {/* LIST */}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                    <CircularProgress />
                </Box>
            ) : filtered.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 10 }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, opacity: 0.4, mb: 2 }}>
                        {automations.length === 0 ? 'Nenhuma automação criada ainda.' : 'Nenhum resultado encontrado.'}
                    </Typography>
                    {automations.length === 0 && (
                        <Button
                            variant="contained"
                            startIcon={<IconPlus size={18} />}
                            onClick={openCreate}
                            sx={{
                                borderRadius: '14px',
                                textTransform: 'none',
                                fontWeight: 700,
                                background: 'linear-gradient(135deg,#2563eb,#7c3aed)'
                            }}
                        >
                            Criar primeira automação
                        </Button>
                    )}
                </Box>
            ) : (
                <Grid container spacing={3}>
                    {filtered.map((a, idx) => {
                        const Icon = TASK_ICONS[a.task_name] || IconClock;
                        const color = TASK_COLORS[a.task_name] || '#64748b';
                        const isRunning = runningId === a.id;

                        return (
                            <Grid item xs={12} md={6} lg={4} key={a.id}>
                                <MotionCard
                                    initial={{ opacity: 0, y: 16 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: idx * 0.04 }}
                                    whileHover={{ y: -4 }}
                                    sx={{
                                        borderRadius: '24px',
                                        border: a.active
                                            ? `1px solid ${color}33`
                                            : isDark
                                              ? '1px solid rgba(255,255,255,0.06)'
                                              : '1px solid rgba(15,23,42,0.06)',
                                        background: isDark ? '#111827' : '#fff',
                                        boxShadow: a.active ? `0 8px 24px ${color}22` : 'none',
                                        opacity: a.active ? 1 : 0.6
                                    }}
                                >
                                    <CardContent sx={{ p: 3 }}>
                                        {/* Header */}
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                justifyContent: 'space-between',
                                                mb: 2
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                <Avatar
                                                    sx={{
                                                        width: 44,
                                                        height: 44,
                                                        flexShrink: 0,
                                                        background: a.active
                                                            ? `linear-gradient(135deg,${color},${color}99)`
                                                            : isDark
                                                              ? '#1e293b'
                                                              : '#f1f5f9'
                                                    }}
                                                >
                                                    <Icon size={20} color={a.active ? '#fff' : color} />
                                                </Avatar>
                                                <Box>
                                                    <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                                                        {a.name}
                                                    </Typography>
                                                    <Chip
                                                        label={a.task_label}
                                                        size="small"
                                                        sx={{
                                                            mt: 0.5,
                                                            height: 18,
                                                            fontSize: 10,
                                                            fontWeight: 700,
                                                            background: `linear-gradient(135deg,${color},${color}99)`,
                                                            color: '#fff',
                                                            '& .MuiChip-label': { px: 0.75 }
                                                        }}
                                                    />
                                                </Box>
                                            </Box>

                                            <Tooltip title={a.active ? 'Desativar' : 'Ativar'}>
                                                <Switch size="small" checked={a.active} onChange={() => handleToggleActive(a)} />
                                            </Tooltip>
                                        </Box>

                                        {a.description && (
                                            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5, fontSize: 13 }}>
                                                {a.description}
                                            </Typography>
                                        )}

                                        <Divider sx={{ my: 1.5 }} />

                                        {/* Schedule info */}
                                        <Stack spacing={0.75} sx={{ mb: 2 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <IconClock size={14} color={color} />
                                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                    {describeSchedule(a)}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <IconHistory size={14} color={color} />
                                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                    {a.total_run_count} execuções · última {formatDate(a.last_run_at)}
                                                </Typography>
                                            </Box>
                                        </Stack>

                                        {/* Actions */}
                                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                            <Tooltip title="Executar agora">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleRunNow(a)}
                                                    disabled={isRunning}
                                                    sx={{
                                                        background: `${color}18`,
                                                        color,
                                                        borderRadius: '10px',
                                                        width: 34,
                                                        height: 34,
                                                        '&:hover': { background: `${color}30` }
                                                    }}
                                                >
                                                    {isRunning ? <CircularProgress size={14} /> : <IconPlayerPlay size={16} />}
                                                </IconButton>
                                            </Tooltip>

                                            <Tooltip title="Histórico">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => openHistory(a)}
                                                    sx={{
                                                        background: isDark ? '#1e293b' : '#f1f5f9',
                                                        borderRadius: '10px',
                                                        width: 34,
                                                        height: 34
                                                    }}
                                                >
                                                    <IconHistory size={16} />
                                                </IconButton>
                                            </Tooltip>

                                            <Tooltip title="Editar">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => openEdit(a)}
                                                    sx={{
                                                        background: isDark ? '#1e293b' : '#f1f5f9',
                                                        borderRadius: '10px',
                                                        width: 34,
                                                        height: 34
                                                    }}
                                                >
                                                    <IconEdit size={16} />
                                                </IconButton>
                                            </Tooltip>

                                            <Tooltip title="Excluir">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setDeleteId(a.id)}
                                                    sx={{
                                                        background: '#ef444418',
                                                        color: '#ef4444',
                                                        borderRadius: '10px',
                                                        width: 34,
                                                        height: 34,
                                                        '&:hover': { background: '#ef444430' }
                                                    }}
                                                >
                                                    <IconTrash size={16} />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </CardContent>
                                </MotionCard>
                            </Grid>
                        );
                    })}
                </Grid>
            )}

            {/* ───────────────────── CREATE / EDIT DIALOG ───────────────────── */}
            <Dialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                fullWidth
                maxWidth="sm"
                PaperProps={{ sx: { borderRadius: '24px' } }}
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 0 }}>
                    <Typography variant="h4" fontWeight={800}>
                        {editingId ? 'Editar automação' : 'Nova automação'}
                    </Typography>
                    <IconButton onClick={() => setDialogOpen(false)}>
                        <IconX size={18} />
                    </IconButton>
                </DialogTitle>

                <DialogContent>
                    <Stack spacing={2.5} sx={{ mt: 2 }}>
                        <TextField
                            label="Nome"
                            fullWidth
                            value={formData.name}
                            onChange={(e) => setField('name', e.target.value)}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '14px' } }}
                        />

                        <TextField
                            label="Descrição (opcional)"
                            fullWidth
                            multiline
                            rows={2}
                            value={formData.description}
                            onChange={(e) => setField('description', e.target.value)}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '14px' } }}
                        />

                        <TextField
                            label="Tipo de tarefa"
                            select
                            fullWidth
                            value={formData.task_name}
                            onChange={(e) => setField('task_name', e.target.value)}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '14px' } }}
                        >
                            {choices.tasks.length > 0 ? (
                                choices.tasks.map((t) => (
                                    <MenuItem key={t.value} value={t.value}>
                                        {t.label}
                                    </MenuItem>
                                ))
                            ) : (
                                <MenuItem disabled>Carregando...</MenuItem>
                            )}
                        </TextField>

                        {formData.task_name === 'automations.tasks.trigger_campaign' && (
                            <Alert severity="info" sx={{ borderRadius: '14px' }}>
                                Essa automação não é pra uma campanha específica - a cada execução, ela dispara
                                automaticamente TODAS as campanhas com status &quot;Agendada&quot; cuja data/hora programada já
                                chegou. Basta agendar a campanha na tela de Marketing; essa automação só precisa rodar
                                periodicamente pra pegá-las no horário certo.
                            </Alert>
                        )}

                        {/* Extra kwargs per task type */}
                        {extraFields.map((f) => (
                            <TextField
                                key={f.key}
                                label={f.label}
                                type={f.type}
                                fullWidth
                                value={formData.kwargs[f.key] ?? f.default}
                                onChange={(e) => setKwarg(f.key, f.type === 'number' ? Number(e.target.value) : e.target.value)}
                                inputProps={{ min: 1 }}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '14px' } }}
                            />
                        ))}

                        <TextField
                            label="Tipo de agendamento"
                            select
                            fullWidth
                            value={formData.schedule_type}
                            onChange={(e) => setField('schedule_type', e.target.value)}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '14px' } }}
                        >
                            <MenuItem value="interval">Intervalo fixo</MenuItem>
                            <MenuItem value="crontab">Cron personalizado</MenuItem>
                        </TextField>

                        {formData.schedule_type === 'interval' ? (
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <TextField
                                    label="A cada"
                                    type="number"
                                    value={formData.interval_every}
                                    onChange={(e) => setField('interval_every', Number(e.target.value))}
                                    inputProps={{ min: 1 }}
                                    sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: '14px' } }}
                                />
                                <TextField
                                    label="Período"
                                    select
                                    value={formData.interval_period}
                                    onChange={(e) => setField('interval_period', e.target.value)}
                                    sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: '14px' } }}
                                >
                                    {choices.period_choices.length > 0
                                        ? choices.period_choices.map((p) => (
                                              <MenuItem key={p.value} value={p.value}>
                                                  {p.label}
                                              </MenuItem>
                                          ))
                                        : ['seconds', 'minutes', 'hours', 'days'].map((p) => (
                                              <MenuItem key={p} value={p}>
                                                  {p}
                                              </MenuItem>
                                          ))}
                                </TextField>
                            </Box>
                        ) : (
                            <Box>
                                <Typography variant="caption" sx={{ color: 'text.secondary', mb: 1, display: 'block' }}>
                                    Expressão cron — minuto · hora · dia da semana · dia do mês · mês
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    {[
                                        { key: 'crontab_minute', label: 'Min' },
                                        { key: 'crontab_hour', label: 'Hora' },
                                        { key: 'crontab_day_of_week', label: 'Dia sem.' },
                                        { key: 'crontab_day_of_month', label: 'Dia mês' },
                                        { key: 'crontab_month_of_year', label: 'Mês' }
                                    ].map((f) => (
                                        <TextField
                                            key={f.key}
                                            label={f.label}
                                            value={formData[f.key]}
                                            onChange={(e) => setField(f.key, e.target.value)}
                                            sx={{
                                                flex: '1 0 72px',
                                                '& .MuiOutlinedInput-root': { borderRadius: '14px' }
                                            }}
                                        />
                                    ))}
                                </Box>
                            </Box>
                        )}

                        <FormControlLabel
                            control={<Switch checked={formData.active} onChange={(e) => setField('active', e.target.checked)} />}
                            label="Ativar imediatamente ao criar"
                        />
                    </Stack>
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
                    <Button variant="outlined" onClick={() => setDialogOpen(false)} sx={{ borderRadius: '12px', textTransform: 'none' }}>
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={saving}
                        sx={{
                            borderRadius: '12px',
                            textTransform: 'none',
                            px: 3,
                            background: 'linear-gradient(135deg,#2563eb,#7c3aed)'
                        }}
                    >
                        {saving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : editingId ? 'Salvar' : 'Criar'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ───────────────────── DELETE CONFIRM ───────────────────── */}
            <Dialog
                open={!!deleteId}
                onClose={() => setDeleteId(null)}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: '20px' } }}
            >
                <DialogTitle>Excluir automação</DialogTitle>
                <DialogContent>
                    <Typography>Tem certeza? Esta ação não pode ser desfeita e cancela o agendamento.</Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button variant="outlined" onClick={() => setDeleteId(null)} sx={{ borderRadius: '12px', textTransform: 'none' }}>
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleDelete}
                        disabled={deleting}
                        sx={{ borderRadius: '12px', textTransform: 'none' }}
                    >
                        {deleting ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Excluir'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ───────────────────── HISTORY DIALOG ───────────────────── */}
            <Dialog
                open={historyOpen}
                onClose={() => setHistoryOpen(false)}
                fullWidth
                maxWidth="md"
                PaperProps={{ sx: { borderRadius: '24px' } }}
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                        <Typography variant="h4" fontWeight={800}>
                            Histórico de execuções
                        </Typography>
                        {historyAutomation && (
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {historyAutomation.name} — {historyAutomation.task_label}
                            </Typography>
                        )}
                    </Box>
                    <IconButton onClick={() => setHistoryOpen(false)}>
                        <IconX size={18} />
                    </IconButton>
                </DialogTitle>

                <DialogContent>
                    {executionsLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                            <CircularProgress />
                        </Box>
                    ) : executions.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 6 }}>
                            <Typography sx={{ opacity: 0.4, fontWeight: 700 }}>Nenhuma execução registrada ainda.</Typography>
                        </Box>
                    ) : (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Início</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Fim</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Resultado / Erro</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {executions.map((ex) => {
                                    const cfg = STATUS_CONFIG[ex.status] || { label: ex.status, color: '#64748b' };
                                    return (
                                        <TableRow key={ex.id} hover>
                                            <TableCell>
                                                <Chip
                                                    label={cfg.label}
                                                    size="small"
                                                    sx={{
                                                        background: `${cfg.color}22`,
                                                        color: cfg.color,
                                                        fontWeight: 700,
                                                        fontSize: 11
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="caption">{formatDate(ex.started_at)}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="caption">{formatDate(ex.finished_at)}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="caption" sx={{ color: ex.error ? '#ef4444' : 'text.primary' }}>
                                                    {ex.error || ex.result || '—'}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    )}
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setHistoryOpen(false)} sx={{ borderRadius: '12px', textTransform: 'none' }}>
                        Fechar
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Automations;
